import React, { useEffect, useMemo, useState } from 'react';
import { TreeNode } from './TreeNode';
import { Box } from '@mui/material';
import Curve_SVG from '../../../../assets/Treeview/curve.svg';
import { roleLabelForDisplay } from 'utils/entityTerminology';

const getTypeByKey = (key) => roleLabelForDisplay(key) || 'Property Manager';

const normalizeId = (value) => String(value || `tmp-${Math.random().toString(36).slice(2)}`);
const getNodeId = (node) =>
  node?.accepter_id ?? node?.accepterId ?? node?.userId ?? node?.id ?? node?.delegation_id ?? null;
const getNodeEmail = (node) =>
  node?.accepter_email ?? node?.accepterEmail ?? node?.email ?? node?.username ?? '';
const getSubtitleByKey = (key) => roleLabelForDisplay(key) || 'Property Manager';
const toMoveNode = (node) => ({
  subtitle: getSubtitleByKey(node.key),
  key: node.key,
  title: node.email,
  id: node.id,
});

const createPMNode = (pm) => ({
  id: normalizeId(getNodeId(pm)),
  email: getNodeEmail(pm),
  key: 'pm',
  type: getTypeByKey('pm'),
});

const createRMNode = (rm) => ({
  id: normalizeId(getNodeId(rm)),
  email: getNodeEmail(rm),
  key: 'rm',
  type: getTypeByKey('rm'),
  children: (rm.pm || []).map(createPMNode),
});

const createVPNode = (vp) => ({
  id: normalizeId(getNodeId(vp)),
  email: getNodeEmail(vp),
  key: 'vp',
  type: getTypeByKey('vp'),
  children: [...(vp.rm || []).map(createRMNode), ...(vp.pm || []).map(createPMNode)],
});

const buildTreeFromRecord = (treeRecord) => {
  const tree = treeRecord?.data?.treeData || {};
  const vpNodes = (tree.vp || []).map(createVPNode);
  const rmNodes = (tree.rm || []).map(createRMNode);
  const pmNodes = (tree.pm || []).map(createPMNode);
  const merged = [...vpNodes, ...rmNodes, ...pmNodes];
  return merged;
};

export const TreeViewer = ({ treeRecord, isEditMode = false, onMovesChange }) => {
  const [treeData, setTreeData] = useState(buildTreeFromRecord(treeRecord));
  const [moves, setMoves] = useState([]);
  const rootKey = treeRecord?.rootKey || 'll';
  const rootNode = useMemo(
    () => ({
      id: normalizeId(getNodeId(treeRecord?.data)),
      key: rootKey,
      email: treeRecord?.email || '',
      type: treeRecord?.rootLabel || getTypeByKey(rootKey),
    }),
    [treeRecord, rootKey],
  );

  useEffect(() => {
    const nextTreeData = buildTreeFromRecord(treeRecord);
    setTreeData(nextTreeData);
    setMoves([]);
    if (onMovesChange) onMovesChange([]);
  }, [treeRecord, onMovesChange]);

  const findItemById = (items, id) => {
    for (let item of items) {
      if (item.id === id) return [item, items];
      if (item.children) {
        const result = findItemById(item.children, id);
        if (result[0]) return result;
      }
    }
    return [null, null];
  };

  const handleDrop = (draggedId, targetId, position) => {
    if (draggedId === targetId) return;

    const [draggedItem, draggedParent] = findItemById(treeData, draggedId);
    const [targetItem, targetParent] = findItemById(treeData, targetId);

    if (!draggedItem || !draggedParent || !targetItem) return;
    if (!isEditMode) return;

    const draggedIndex = draggedParent.indexOf(draggedItem);
    draggedParent.splice(draggedIndex, 1);

    if (position === 'inside') {
      if (!targetItem.children) targetItem.children = [];
      targetItem.children.push(draggedItem);
      targetItem.isExpanded = true;
    } else {
      const targetArray = targetParent || treeData;
      const targetIndex = targetArray.indexOf(targetItem);
      targetArray.splice(position === 'before' ? targetIndex : targetIndex + 1, 0, draggedItem);
    }

    const movedToNode = position === 'inside' ? targetItem : targetParent || rootNode;
    const moveItem = {
      node: toMoveNode(draggedItem),
      moveto: toMoveNode(movedToNode),
    };
    if (moveItem.node.id && moveItem.moveto.id) {
      setMoves((existing) => {
        const foundIndex = existing.findIndex((item) => item.node.id === moveItem.node.id);
        if (foundIndex === -1) return [...existing, moveItem];
        const next = [...existing];
        next[foundIndex] = moveItem;
        return next;
      });
    }

    setTreeData([...treeData]);
  };

  const handleToggle = (id) => {
    const [item] = findItemById(treeData, id);
    if (item) {
      item.isExpanded = !item.isExpanded;
      setTreeData([...treeData]);
    }
  };

  useEffect(() => {
    localStorage.setItem('treeData', JSON.stringify(treeData));
  }, [treeData]);

  useEffect(() => {
    if (onMovesChange) onMovesChange(moves);
  }, [moves, onMovesChange]);

  return (
    <Box style={{ padding: '18px 0 18px 0', zIndex: '-1', backgroundColor: '#F8F4F952' }}>
      {treeData.map((item, index) => (
        <Box key={item.id || index} style={{ position: 'relative' }}>
          <img
            src={Curve_SVG}
            alt="Curve"
            style={{ zIndex: '1', position: 'absolute', left: '46px', top: '-45px' }}
          />
          <TreeNode
            key={item.id}
            item={item}
            level={0}
            onDrop={handleDrop}
            onToggle={handleToggle}
            parentId={null}
            index={index}
            isEditMode={isEditMode}
          />
        </Box>
      ))}
    </Box>
  );
};
